import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { Crown, Trophy, CalendarDays, ImageOff, ArrowRight, Sparkles } from "lucide-react";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
function monthLabel(m) {
  if (!m) return "";
  const [y, mm] = m.split("-");
  return `${MONTHS[parseInt(mm, 10) - 1]} ${y}`;
}

export default function Winners() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/raffles/winners?limit=12")
      .then((r) => setItems(r.data || []))
      .catch((err) => {
        console.warn("Winners: failed to load", err?.message);
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-10">
        <div className="overline text-amber-500">GANADORES · RIFA OFERTAS MÉXICO</div>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight mt-2 leading-[1]">
          Esto sí es <span className="bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">real</span>
        </h1>
        <p className="text-muted-foreground mt-3 text-base max-w-2xl">
          Cada mes rifamos electrodomésticos entre los clientes que compran en comercios OFERTAS. Aquí están los ganadores recientes con la foto del momento de la entrega.
        </p>
      </section>

      {loading ? (
        <div className="max-w-7xl mx-auto px-6 pb-20 text-muted-foreground">Cargando ganadores…</div>
      ) : items.length === 0 ? (
        <section className="max-w-7xl mx-auto px-6 pb-20">
          <div className="bg-white border border-border rounded-3xl p-12 text-center" data-testid="winners-empty">
            <Trophy className="w-12 h-12 mx-auto text-amber-400" />
            <div className="font-display text-3xl mt-4">Próximamente</div>
            <p className="text-muted-foreground mt-2 max-w-md mx-auto">
              Aún no hemos realizado el primer sorteo. ¡Suma tus boletos comprando en comercios OFERTAS y sé el primer ganador!
            </p>
            <Link to="/registro-cliente" className="btn-primary inline-flex items-center gap-2 mt-6 rounded-full px-6 py-3 font-semibold">
              Crear mi cuenta <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      ) : (
        <section className="max-w-7xl mx-auto px-6 pb-20 space-y-10" data-testid="winners-list">
          {items.map((r) => (
            <div key={r.month} className="bg-white border border-border rounded-3xl overflow-hidden" data-testid={`winners-month-${r.month}`}>
              <div className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 px-6 md:px-8 py-5 flex items-center justify-between flex-wrap gap-3 text-amber-950">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[#5B21B6] grid place-items-center">
                    <Crown className="w-5 h-5 text-amber-300" />
                  </div>
                  <div>
                    <div className="overline text-[#5B21B6] text-[11px]">Rifa de</div>
                    <div className="font-display text-2xl">{monthLabel(r.month)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CalendarDays className="w-4 h-4" />
                  <span>
                    Sorteo realizado el <strong>{r.drawn_at ? new Date(r.drawn_at).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" }) : "—"}</strong>
                  </span>
                </div>
              </div>

              <div className="p-6 md:p-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {r.winners.map((w) => (
                  <div key={w.position} className="border border-border rounded-2xl overflow-hidden bg-gradient-to-b from-white to-[#FAFAFA] flex flex-col" data-testid={`winner-card-${r.month}-${w.position}`}>
                    <WinnerPhoto src={w.photo_base64} alt={`Entrega de ${w.prize_label}`} />
                    <div className="p-5 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-amber-300 grid place-items-center text-[#5B21B6] font-display font-bold text-sm shrink-0">
                          {w.position}
                        </div>
                        <div className="overline text-[10px] text-amber-600">PREMIO {w.position}</div>
                      </div>
                      <div className="font-display text-xl tracking-tight mt-2 leading-tight">
                        {w.prize_label}
                      </div>
                      <div className="mt-3 border-t border-border pt-3">
                        <div className="text-xs text-muted-foreground">Ganador:</div>
                        <div className="font-medium">{w.winner_first_name}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{w.winner_phone_mask}</div>
                        {w.business_name && (
                          <div className="text-[11px] text-muted-foreground mt-2">
                            Boleto ganador adquirido en: <strong className="text-[#5B21B6]">{w.business_name}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-[#7C3AED] via-[#6D28D9] to-[#4C1D95] text-white p-10 md:p-14 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full bg-amber-300/20 blur-3xl pointer-events-none" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <Sparkles className="w-8 h-8 text-amber-300" />
              <h2 className="font-display text-4xl tracking-tight mt-3 leading-tight">
                ¿Quieres ser el siguiente?
              </h2>
              <p className="text-white/85 mt-3 text-sm max-w-md">
                Crea tu cuenta gratis, compra en cualquier comercio OFERTAS y por cada compra recibes 1 boleto digital para la rifa del mes. Más compras = más boletos = más posibilidades de ganar.
              </p>
            </div>
            <div className="flex md:justify-end gap-3 flex-wrap">
              <Link to="/registro-cliente" className="bg-amber-300 text-[#3B1565] rounded-full px-6 py-3.5 font-semibold hover:bg-amber-200 transition-colors inline-flex items-center gap-2" data-testid="winners-cta-register">
                Crear cuenta gratis <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/directorios" className="bg-white/10 backdrop-blur border border-white/30 text-white rounded-full px-6 py-3.5 font-medium hover:bg-white/20 transition-colors inline-flex items-center gap-2">
                Ver comercios cerca
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function WinnerPhoto({ src, alt }) {
  const [errored, setErrored] = useState(false);
  if (src && !errored) {
    return (
      <div className="aspect-[4/3] bg-zinc-100 overflow-hidden">
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setErrored(true)}
        />
      </div>
    );
  }
  return (
    <div className="aspect-[4/3] bg-gradient-to-br from-[#F5F3FF] to-[#EDE9FE] grid place-items-center text-[#5B21B6]">
      <div className="text-center">
        <ImageOff className="w-8 h-8 mx-auto opacity-50" />
        <div className="text-[11px] mt-1 opacity-70">Foto próximamente</div>
      </div>
    </div>
  );
}
